/**
 * Computes diff highlight ranges as ProseMirror document positions.
 *
 * Given the rendered text of the previous and new artifact bodies, this
 * walks the new doc to build a text-offset → doc-position map, runs a
 * semantic diff, and returns one `{from, to}` range per inserted region.
 *
 * The diff runs at *rendered text* level (`doc.textContent`) rather than
 * markdown source level, so `**bold**` syntax characters don't pollute
 * the diff and the resulting highlight bands fall on the rendered prose
 * the user actually sees.
 *
 * @module lib/artifacts/diff-positions
 */

import { diff_match_patch as DiffMatchPatch } from "diff-match-patch";
import type { Node as ProseMirrorNode } from "prosemirror-model";

const DIFF_EQUAL = 0;
const DIFF_INSERT = 1;

/** ProseMirror inline range to highlight. */
export interface DiffRange {
  from: number;
  to: number;
}

/**
 * Internal mapping from rendered-text offset to ProseMirror doc position.
 * Built by walking text nodes top-to-bottom; each entry covers one text
 * node's character span.
 */
interface OffsetMapEntry {
  textStart: number;
  textEnd: number;
  docStart: number;
}

/**
 * Walks the doc and produces a map from text offset (as it would appear
 * in `doc.textContent`) to ProseMirror doc position.
 *
 * `doc.textContent` concatenates text-node contents with no separators,
 * so block boundaries don't add anything to the offset count. Decoration
 * positions however need the doc position which DOES count those gaps —
 * the per-text-node entries record the doc-pos start so we can compute
 * `docStart + (offset - textStart)` for any offset within a node.
 */
function buildOffsetMap(doc: ProseMirrorNode): OffsetMapEntry[] {
  const map: OffsetMapEntry[] = [];
  let textOffset = 0;
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      map.push({
        textStart: textOffset,
        textEnd: textOffset + node.text.length,
        docStart: pos,
      });
      textOffset += node.text.length;
    }
  });
  return map;
}

/**
 * Translates a rendered-text offset into a ProseMirror doc position.
 *
 * Offsets that land at the boundary between two text nodes resolve to
 * the *start* of the next node — which is what we want when wrapping
 * highlight ranges, because the range should include the next node's
 * leading character.
 */
function textOffsetToDocPos(map: OffsetMapEntry[], offset: number): number {
  for (const entry of map) {
    if (offset >= entry.textStart && offset <= entry.textEnd) {
      return entry.docStart + (offset - entry.textStart);
    }
  }
  // Offset past the last text node — clamp to end of document text.
  const last = map[map.length - 1];
  return last ? last.docStart + (last.textEnd - last.textStart) : 0;
}

/**
 * Public entry point. Diffs the previous rendered text against the new
 * doc's rendered text and returns one inline-decoration range per insert.
 *
 * Whitespace-only inserts (extra blank lines) are skipped — the band
 * would be invisible. Whitespace at the leading/trailing edges of an
 * insert is excluded from the highlight range so paragraph boundaries
 * don't get visually included in the band.
 *
 * @param previousText - `previousDoc.textContent` from before the update
 * @param newDoc - the doc *after* the update content was set
 * @returns an array of doc-position ranges suitable for `setDiffHighlight`
 */
export function computeDiffRanges(
  previousText: string,
  newDoc: ProseMirrorNode,
): DiffRange[] {
  const newText = newDoc.textContent;
  if (!previousText || previousText === newText) return [];

  const dmp = new DiffMatchPatch();
  const diffs = dmp.diff_main(previousText, newText);
  // Semantic cleanup groups inserts into human-readable chunks rather
  // than character-by-character noise. Critical so the bands feel calm.
  dmp.diff_cleanupSemantic(diffs);

  const map = buildOffsetMap(newDoc);
  const ranges: DiffRange[] = [];
  let cursor = 0;

  for (const [op, text] of diffs) {
    if (op === DIFF_EQUAL) {
      cursor += text.length;
      continue;
    }
    if (op === DIFF_INSERT) {
      if (text.trim().length === 0) {
        cursor += text.length;
        continue;
      }
      // Strip whitespace from band edges so paragraph breaks don't get
      // visually wrapped.
      const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(text);
      if (match) {
        const [, lead, body] = match;
        const innerStart = cursor + lead.length;
        const innerEnd = innerStart + body.length;
        const fromDoc = textOffsetToDocPos(map, innerStart);
        const toDoc = textOffsetToDocPos(map, innerEnd);
        if (fromDoc < toDoc) ranges.push({ from: fromDoc, to: toDoc });
      }
      cursor += text.length;
    }
    // DIFF_DELETE: chars exist in previousText but not in newText — skip.
  }
  return ranges;
}
